import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select"
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, InputAdornment } from '@mui/material';
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons and images
import { grey } from '@mui/material/colors';
import SearchIcon from "common/customIcons/search";
import AngleRightIcon from "common/customIcons/angle_right";
import defaultImage from 'assets/images/placeholder-image.png';
// utils and helpers
import { debounce, compareJson } from "utilities/common";
import { validateBox } from "validations/hyperwolf/distributionmanagement";
import { createBox, getBoxDetails, getUnassignedProducts, updateBox } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from 'components/MDButton';
import { SubCategoryFilter, BrandFilter } from "common/filters";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import { productsTypeOptions } from "../index";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

const initialValues = {
  name: "",
  description: "",
  isActive: true,
}

const CreateBox = () => {
  const gridRef = useRef();
  const { boxId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector(state => state.auth);
  const [oldDetails, setOldDetails] = useState({})
  const [details, setDetails] = useState({ ...initialValues })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [oldSelectedProducts, setOldSelectedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Product Name/SKU',
      field: 'adminProductName',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 300,
      sortable: true,
      checkboxSelection: true,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { adminProductName, sku, assets } = params?.data || {};
        const { thumbURL, name } = assets?.[0] || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          <img src={thumbURL || defaultImage} alt={name} width={30} height={30} />
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{adminProductName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Category',
      field: 'categoryName',
      suppressMenu: false,
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: ({ data }) => data?.category?.name || "-",
    },
    {
      headerName: 'Brand',
      field: 'brandName',
      suppressMenu: false,
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: ({ data }) => data?.brand?.name || "-",
    },
    {
      headerName: 'Box',
      field: 'boxName',
      suppressMenu: false,
      sortable: true,
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { box } = params?.data || {}
        return <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{box?.name || "-"}</Typography>
      }
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      suppressMenu: false,
      sortable: true,
      flex: 1,
      minWidth: 150,
      headerClass: 'jc-start',
      valueGetter: ({ data }) => data?.quantity || 0
    },
  ], []);

  const handleGetAllProducts = async () => {
    try {
      if (gridRef.current) {
        gridRef.current?.api?.showLoadingOverlay();
      }

      const response = await dispatch(getUnassignedProducts({ limit: 100000, page: 1, boxId: boxId || "" })).unwrap()
      const { data } = response || {};
      if (!data?.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
      if (data) {
        setAllProducts(data || [])
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  }

  const handleBoxDetails = async () => {
    try {
      const response = await dispatch(getBoxDetails({ boxId })).unwrap()
      if (response?.data) {
        setDetails({ ...response.data })
        setOldDetails({ ...response.data })
        setOldSelectedProducts(response?.data?.productIds || []);
        setSelectedProducts(response?.data?.productIds || []);
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));

    if (isAllChecked) {
      setIsAllChecked(false);
    }
  }

  const handleSearch = useCallback(debounce((value) => {
    gridRef.current?.api?.setGridOption('quickFilterText', value);
  }, 500), []);

  const handleClearFilters = () => {
    setSearchTerm("");
    setCustomFilters({});
    gridRef.current?.api?.setGridOption('quickFilterText', "");
    setTimeout(() => {
      gridRef.current?.api?.onFilterChanged();
    }, 0);
    setIsAllChecked(false);
  }

  const handleTopLevelChange = (field) => (event) => {
    const valueRaw = event.target.value;
    let value = valueRaw;
    setDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (isLoading) return;

      let request = {
        ...details,
        createdBy: user?.name,
        productIds: selectedProducts,
        isAllSelected: false,
      };

      if (isAllChecked) {
        const { categoryId, brandId } = customFilters || {}
        request = { ...request, categoryId, brandId }
      }

      const { isValid, myErrors } = validateBox(request);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }
      setIsLoading(true);

      let response = {};
      if (boxId) {
        response = await dispatch(updateBox(request)).unwrap();
      } else {
        response = await dispatch(createBox(request)).unwrap();
      }

      if (response?.data || response?.message) {
        showToastMsg(response.message || "Success", "success")
        navigate(`/hyperwolf/boxes`);
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;
    setIsAllChecked(checked);

    if (checked) {
      gridRef.current?.api?.selectAllFiltered();
    } else {
      gridRef.current?.api?.forEachNodeAfterFilter((node) => node.setSelected(false));
    }
  };

  const isExternalFilterPresent = useCallback(() => {
    return Object.values(customFilters).some(Boolean);
  }, [customFilters]);

  const doesExternalFilterPass = useCallback((node) => {
    const { categoryId, brandId, productsType } = customFilters;
    const data = node.data;

    if (!data) return false;

    if (categoryId) {
      const catId = data.category?.id;
      if (catId !== categoryId) return false;
    }

    if (brandId) {
      const bId = data.brand?.id;
      if (bId !== brandId) return false;
    }

    if (productsType === "assigned") {
      if (!data.isAssigned && !data.isSelected) return false;
    } else if (productsType === "unassigned") {
      if (data.isAssigned || data.isSelected) return false;
    }

    return true;
  }, [customFilters]);

  const updateIsAllChecked = useCallback(() => {
    const selectableDisplayedRows = [];
    gridRef.current?.api?.forEachNodeAfterFilterAndSort((node) => {
      // if (!node.data.isAssigned) {
      selectableDisplayedRows.push(node);
      // }
    });

    const allSelectableSelected = selectableDisplayedRows.length > 0 && selectableDisplayedRows.every(node => node.isSelected());
    setIsAllChecked(allSelectableSelected);
  }, []);

  const onSelectionChanged = useCallback(debounce(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows();
    const selectedIds = selectedRows?.map(row => row.productId) || [];
    setSelectedProducts(selectedIds);

    // Update Select All checkbox state based on selection
    updateIsAllChecked();
  }, 100), [updateIsAllChecked]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.onFilterChanged();
    }
  }, [customFilters]);

  // Pre-select rows when data loads or edit mode
  useEffect(() => {
    if (allProducts.length > 0 && gridRef.current?.api) {
      // Wait for grid to be ready
      setTimeout(() => {
        gridRef.current.api.forEachNode((node) => {
          if (selectedProducts.includes(node.data.productId)) {
            node.setSelected(true);
          }
        });
      }, 100);
    }
  }, [allProducts, gridRef.current]); // Re-run if products change (e.g. load)

  useEffect(() => {
    if (boxId) {
      handleBoxDetails();
    }
  }, [boxId])

  useEffect(() => {
    handleGetAllProducts();
  }, [])

  const isChangesMade = useMemo(() => {
    if (!boxId) return true;

    const isDetailsSame = compareJson(oldDetails, details)
    const isProductsSame = oldSelectedProducts.length === selectedProducts.length &&
      oldSelectedProducts.every(prod => selectedProducts.includes(prod)) &&
      selectedProducts.every(prod => oldSelectedProducts.includes(prod));

    return !isDetailsSame || !isProductsSame;
  }, [oldDetails, details, boxId, oldSelectedProducts, selectedProducts]);

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Box Details
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container spacing={2}>

            {/* Basic details */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Box Name</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    className="form-input bg-transparent"
                    placeholder="Name"
                    name="name"
                    value={details.name}
                    onChange={handleTopLevelChange('name')}
                  />
                  {errors?.name && <p className="error">{errors?.name}</p>}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Description</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    className="form-input"
                    placeholder="Add description..."
                    name='metaDescription'
                    value={details.description}
                    onChange={handleTopLevelChange('description')}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <hr className='my-3' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
            </Grid>

            {/* Products heading and filters */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="semibold" mb={2}>
                {boxId ? "Selected Products" : "Select Products"} ({selectedProducts?.length})
              </Typography>

              {/* Filters */}
              <Box className='cus-filter-sec filter-gray-bg'>
                <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <Select
                      className='cus-react-select'
                      name="productsType"
                      value={productsTypeOptions?.find(type => type.value === customFilters?.productsType)}
                      onChange={selectedOption => {
                        setCustomFilters(prev => ({ ...prev, productsType: selectedOption?.value }))
                      }}
                      options={productsTypeOptions}
                      placeholder="All Products"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: 40,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                      components={{ IndicatorSeparator: null }}
                    />
                  </Grid>

                  <Grid item xs={12} lg={3} xxxl={2} className='p-remove'>
                    <SubCategoryFilter
                      value={customFilters?.categoryId}
                      handleChange={handleFilterChange}
                      placeholder={"categories"}
                      selectAllLabel={"All Categories"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <BrandFilter
                      value={customFilters?.brandId}
                      handleChange={handleFilterChange}
                      placeholder={"Brands"}
                      selectAllLabel={"All"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "center" }}
                      justifyContent="end"
                      style={{ width: "100%" }}
                      className="filter-search"
                    >
                      <Box className="search-gray">
                        <TextField
                          className="custom-search-field"
                          placeholder="Search"
                          value={searchTerm || ""}
                          onChange={e => {
                            const { value } = e.target;
                            setSearchTerm(value);
                            handleSearch(value);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">
                              <IconButton size='small'>
                                <SearchIcon width='18px' height='18px' />
                              </IconButton>
                            </InputAdornment>
                          }}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <MDButton
                      sx={{ minWidth: "120px", fontWeight: "normal" }}
                      type="button"
                      variant="outlined"
                      color="dark"
                      disabled={!searchTerm && !Object.values(customFilters || {})?.filter(Boolean)?.length}
                      onClick={handleClearFilters}
                    >
                      Clear All
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Assign products */}
            <Grid item xs={12} className="table-border-gray table-nodata-size">
              {/* Select all */}
              <Box className="form-check px-3 pb-2 d-flex align-item-center gap-2 text-14">
                <input
                  className="form-check-input cur-pointer"
                  type="checkbox"
                  id={"selectAll"}
                  name={"isAllSelected"}
                  checked={isAllChecked}
                  onChange={handleSelectAll}
                />
                <label className="form-check-label" htmlFor={"selectAll"}>
                  Select All
                </label>
              </Box>

              {/* Products listing */}
              <div className="ag-theme-quartz" style={{ height: "450px", width: "100%" }}>
                <AgGridReact
                  ref={gridRef}
                  className="ag-grid--table"
                  pagination={false}
                  rowData={allProducts?.length > 0 ? allProducts : null}
                  noRowsOverlayComponent={NoDataFound}
                  loadingOverlayComponent={TableLoader}
                  columnDefs={columnDefs}
                  rowModelType={"clientSide"}
                  headerHeight={36}
                  rowHeight={56}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={{ ...defaultColDef }}
                  isExternalFilterPresent={isExternalFilterPresent}
                  doesExternalFilterPass={doesExternalFilterPass}
                  onSelectionChanged={onSelectionChanged}
                  onFilterChanged={updateIsAllChecked}
                  onRowDataUpdated={updateIsAllChecked}
                // getRowStyle={(params) => {
                //   if (params.data?.isAssigned) {
                //     return { pointerEvents: 'none', opacity: 0.7, background: '#f5f5f5' };
                //   }
                //   return null;
                // }}
                // isRowSelectable={(params) => !params.data?.isAssigned}
                />
              </div>
              {errors?.productIds && <p className="error">{errors?.productIds}</p>}
            </Grid>
          </Grid>

          {/********************** Save, Cancel Buttons **********************/}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={6} mb={2}>
                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading || !isChangesMade}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </MDButton>
                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MDButton>
              </Stack>
            </Grid>
          </Grid>
        </Card>

      </Box>
    </CommonLayout>
  )
}

export default CreateBox
